import dateFormat from 'dateformat';
import { milliseconds } from 'date-fns';
import {
  convertTemperature,
  convertTemperatureUnit,
  getDateTimeFormat,
  isUndefinedOrNull,
  getMillisecondsFromTimeDelta,
  areAllObjectValuesEmpty
} from '#helper';

export default (values, props) => {
  const { account, minMaxDate } = props;
  const errors = {};
  const MAX_CUSTOM_FIELDS_LENGTH = 100;

  if (!values.title) {
    errors.title = 'Required';
  } else if (values.title.length < 2) {
    errors.title = 'Must be 2 characters or more';
  }

  if (!Date.parse(values.beginDate)) {
    errors.beginDate = 'Required';
  } else if (minMaxDate?.minDate && new Date(values.beginDate) < new Date(minMaxDate.minDate)) {
    errors.beginDate =
      `Must be later than parent project start date ${dateFormat(minMaxDate.minDate, getDateTimeFormat(account?.measurementSystem, 'dateShort'))}`;
  } else if (minMaxDate?.maxDate && new Date(values.beginDate) > new Date(minMaxDate.maxDate)) {
    errors.beginDate =
      `Must be earlier than parent project end date ${dateFormat(minMaxDate.maxDate, getDateTimeFormat(account?.measurementSystem, 'dateShort'))}`;
  }

  if (values.isExpirationDate === '1') {
    if (!Date.parse(values.expirationDate)) {
      errors.expirationDate = 'Required';
    } else if (Date.parse(values.beginDate) && values.expirationDate < values.beginDate) {
      errors.expirationDate =
        `Must be later than "Begin date": ${dateFormat(values.beginDate, getDateTimeFormat(account?.measurementSystem, 'dateShort'))}`;
    } else if (minMaxDate?.maxDate && new Date(values.expirationDate) >
    new Date(minMaxDate.maxDate)) {
      errors.expirationDate =
        `Must be earlier than parent project end date ${dateFormat(minMaxDate.maxDate, getDateTimeFormat(account?.measurementSystem, 'dateShort'))}`;
    }
  }

  if (!values.users?.length) {
    errors.users = 'Required';
  }

  if (values.description?.length && values.description?.length > 512) {
    errors.description = 'Max 512 characters';
  }

  // Alerts validation

  const alertsArrayErrors = [];
  if (values.alerts?.length) {
    const MAX_DAYS = 49;
    const MAX_DAYS_IN_MILLISECONDS = milliseconds({ days: MAX_DAYS });

    values.alerts.forEach((alert, index) => {
      const fieldErrors = {};

      if (!alert.label) {
        fieldErrors.label = { _error: 'Required' };
      } else if (alert.label.length < 2) {
        fieldErrors.label = { _error: 'Min 2 characters' };
      } else if (alert.label.length > 255) {
        fieldErrors.label = { _error: 'Max 255 characters' };
      }

      if (isUndefinedOrNull(alert.startDelay) || areAllObjectValuesEmpty(alert.startDelay)) {
        fieldErrors.startDelay = { _error: 'Required' };
      } else {
        const totalMilliseconds = getMillisecondsFromTimeDelta(alert.startDelay);
        if (totalMilliseconds > MAX_DAYS_IN_MILLISECONDS) {
          fieldErrors.startDelay = { _error: `Maximum ${MAX_DAYS} days` };
        }
      }

      if (isUndefinedOrNull(alert.minAlertPeriod) ||
        areAllObjectValuesEmpty(alert.minAlertPeriod)) {
        fieldErrors.minAlertPeriod = { _error: 'Required' };
      } else if (alert.minAlertPeriod) {
        const totalMilliseconds = getMillisecondsFromTimeDelta(alert.minAlertPeriod);
        if (totalMilliseconds > MAX_DAYS_IN_MILLISECONDS) {
          fieldErrors.minAlertPeriod = { _error: `Maximum ${MAX_DAYS} days` };
        }
      }

      const numericFields = ['tMin', 'tMax', 'hMin', 'hMax', 'bMin'];
      const tMin = alert.tMin === '' ? +'a' : +alert.tMin;
      const tMax = alert.tMax === '' ? +'a' : +alert.tMax;
      const hMin = alert.hMin === '' ? +'a' : +alert.hMin;
      const hMax = alert.hMax === '' ? +'a' : +alert.hMax;

      if (numericFields.every(key => isUndefinedOrNull(alert[key]) || alert[key].isNaN || alert[key] === '')) {
        numericFields.forEach(key => {
          fieldErrors[key] = { _error: 'At least one field is required' };
        });
      }

      if (!isUndefinedOrNull(tMin) && !isUndefinedOrNull(tMax) &&
        !tMin.isNaN && !tMax.isNaN && tMin > tMax
      ) {
        fieldErrors.tMin = { _error: `Max ${convertTemperature(alert.tMax, account?.measurementSystem)}${convertTemperatureUnit(account?.measurementSystem)}` };
        fieldErrors.tMax = { _error: `Min ${convertTemperature(alert.tMin, account?.measurementSystem)}${convertTemperatureUnit(account?.measurementSystem)}` };
      }
      ['tMin', 'tMax'].forEach(key => {
        if (!isUndefinedOrNull(alert[key]) && !alert[key].isNaN && alert[key] !== '') {
          if (alert[key] < -200) {
            fieldErrors[key] = { _error: `Min ${convertTemperature(-200, account?.measurementSystem)}${convertTemperatureUnit(account?.measurementSystem)}` };
          } else if (alert[key] > 124.9) {
            fieldErrors[key] = { _error: `Max ${convertTemperature(124.9, account?.measurementSystem)}${convertTemperatureUnit(account?.measurementSystem)}` };
          }
        }
      });

      if (!isUndefinedOrNull(hMin) && !isUndefinedOrNull(hMax) &&
      !hMin.isNaN && !hMax.isNaN && hMin > hMax
      ) {
        fieldErrors.hMin = { _error: `Max ${hMax}%` };
        fieldErrors.hMax = { _error: `Min ${hMin}%` };
      }
      ['hMin', 'hMax'].forEach(key => {
        if (!isUndefinedOrNull(alert[key]) && !alert[key].isNaN && alert[key] !== '') {
          if (alert[key] < 0.1) {
            fieldErrors[key] = { _error: `Min ${0.1}%` };
          } else if (alert[key] > 99.9) {
            fieldErrors[key] = { _error: `Max ${99.9}%` };
          }
        }
      });

      if (!isUndefinedOrNull(alert.bMin) && !alert.bMin.isNaN && alert.bMin !== '') {
        if (alert.bMin < 1) {
          fieldErrors.bMin = { _error: `Min ${1}%` };
        } else if (alert.bMin > 99.9) {
          fieldErrors.bMin = { _error: `Max ${99.9}%` };
        }
      }

      alertsArrayErrors[index] = fieldErrors;
    });
  }

  if (alertsArrayErrors.length) {
    errors.alerts = alertsArrayErrors;
  }

  // Alert confirmations validation
  const alertConfirmationErrors = [];
  if (values.alertConfirmations?.length) {
    values.alertConfirmations.forEach((alertConfirmation, index) => {
      const fieldErrors = {};

      if (!alertConfirmation.value) {
        fieldErrors.value = { _error: 'Required' };
      } else if (alertConfirmation.value.length < 2) {
        fieldErrors.value = { _error: 'Min 2 characters' };
      } else if (alertConfirmation.value.length > 255) {
        fieldErrors.value = { _error: 'Max 255 characters' };
      }

      alertConfirmationErrors[index] = fieldErrors;
    });
  }

  if (alertConfirmationErrors.length) {
    errors.alertConfirmations = alertConfirmationErrors;
  }

  // Products validation

  const productsArrayErrors = [];
  if (values.products?.length) {
    values.products.forEach((product, index) => {
      const fieldErrors = {};

      if (!product.label) {
        fieldErrors.label = { _error: 'Required' };
      } else if (product.label.length < 2) {
        fieldErrors.label = { _error: 'Min 2 characters' };
      } else if (product.label.length > 255) {
        fieldErrors.label = { _error: 'Max 255 characters' };
      }

      productsArrayErrors[index] = fieldErrors;
    });
  }

  if (productsArrayErrors.length) {
    errors.products = productsArrayErrors;
  }

  // Package types validation

  const packagesArrayErrors = [];
  if (values.packages?.length) {
    values.packages.forEach((pack, index) => {
      const fieldErrors = {};

      if (!pack.label) {
        fieldErrors.label = { _error: 'Required' };
      } else if (pack.label.length < 2) {
        fieldErrors.label = { _error: 'Min 2 characters' };
      } else if (pack.label.length > 255) {
        fieldErrors.label = { _error: 'Max 255 characters' };
      }

      if (!pack.length) {
        fieldErrors.length = { _error: 'Required' };
      }
      if (!pack.width) {
        fieldErrors.width = { _error: 'Required' };
      }

      packagesArrayErrors[index] = fieldErrors;
    });
  }

  if (packagesArrayErrors.length) {
    errors.packages = packagesArrayErrors;
  }

  // Custom fields validation
  const customFieldsErrors = [];
  if (values.customFields?.length) {
    values.customFields.forEach((customField, index) => {
      const fieldErrors = {};
      if (!customField.label) {
        fieldErrors.label = { _error: 'Required' };
      }

      const customFieldValuesErrors = [];

      customField.customFieldValues?.forEach((fieldValue, valueFieldIndex, self) => {
        const { customFieldValue } = fieldValue;
        const fieldErrorDescription = {};

        if (valueFieldIndex > 0) {
          const prev = self[valueFieldIndex - 1];

          if (prev.customFieldValue === customFieldValue) {
            fieldErrorDescription.customFieldValue = { _error: 'Values should be unique' };
          }
        }

        customFieldValuesErrors[valueFieldIndex] = fieldErrorDescription;
      });

      if (customFieldValuesErrors.length) {
        fieldErrors.customFieldValues = customFieldValuesErrors;
      }

      customFieldsErrors[index] = fieldErrors;
    });
  }

  if (values.customFields?.length > MAX_CUSTOM_FIELDS_LENGTH) {
    errors.customFields = { _error: `Error: Maximum ${MAX_CUSTOM_FIELDS_LENGTH} fields.` };
  } else if (customFieldsErrors.length) {
    errors.customFields = customFieldsErrors;
  }

  return errors;
};
